/**
 * @fileoverview Convert summary HTML into clipboard-friendly plain text.
 *
 * `innerText` on a detached element follows the HTML Standard and returns
 * descendant text content, so adjacent `<p>` / `<br>` nodes collapse into
 * one line. This helper inserts explicit newlines first, then reads
 * `textContent`. When `document` is unavailable, a tag-stripping fallback
 * still inserts paragraph and `<br>` breaks so the contract stays the same.
 */

/** Tag-stripping fallback for SSR / Node, matching the browser newline rules. */
function convertHtmlToCopyTextWithoutDocument(html: string): string {
  const withBreaks = html.replace(/<br\s*\/?>/gi, "\n");
  const withBlocks = withBreaks.replace(/<\/(?:p|li)>/gi, "\n\n");
  const withoutTags = withBlocks.replace(/<[^>]*>/g, " ");
  const collapsedSpaces = withoutTags.replace(/[^\S\n]+/g, " ");
  const tidyNewlines = collapsedSpaces.replace(/ *\n */g, "\n");
  return tidyNewlines.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * Convert sanitised summary HTML to clipboard text, preserving paragraph
 * and line-break boundaries.
 *
 * @param html - Sanitised HTML from the background summary card.
 * @returns Plain text with paragraph breaks, trimmed.
 *
 * @example
 * convertHtmlToCopyText("<p>First.</p><p>Second.</p>");
 * // "First.\n\nSecond."
 */
export function convertHtmlToCopyText(html: string): string {
  if (typeof document === "undefined") {
    return convertHtmlToCopyTextWithoutDocument(html);
  }

  const el = document.createElement("div");
  el.innerHTML = html;

  for (const breakElement of el.querySelectorAll("br")) {
    breakElement.replaceWith("\n");
  }
  for (const blockElement of el.querySelectorAll("p, li")) {
    blockElement.after("\n\n");
  }

  return (el.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}
